import {AlertIcon, CommentIcon, InfoIcon, XCircleFillIcon} from '@primer/octicons-react'
import {memo, useCallback, useEffect, useRef, useMemo, type MouseEvent, type KeyboardEvent} from 'react'
import {Tooltip} from '@primer/react/next'
import {Link, TreeView} from '@primer/react'
import type {DiffDelta, FileNode} from '../diff-file-tree-helpers'
import {FileStatusIcon} from '../FileStatusIcon'
import {useFileTreeTooltip} from '@github-ui/use-file-tree-tooltip'
import {ssrSafeDocument, ssrSafeWindow} from '@github-ui/ssr-utils'
import {
  useIsSelectedFile,
  useUpdateSelectedPathDigestAndFetch,
} from '@github-ui/pull-requests/pull-request-store-selectors'
import styles from '../DiffFileTree.module.css'
import {addUrlToHistoryStack, updateUrlHash} from '@github-ui/history'
import {scrollToElementWithOffset} from '../scroll-helpers'
import {getGlobalINPObserver} from '@github-ui/web-vitals'
import {sendCustomMetric} from '@github-ui/stats'
import {useAnalytics} from '@github-ui/use-analytics'
import {useGetINPDiffTags} from '@github-ui/pull-requests/pull-requests-analytics-store-selectors'

export const DIFF_FILE_TREE_ID = 'diff_file_tree'

export interface FileProps {
  file: FileNode<DiffDelta>
  depth: number
  onSelect?: () => void
}

const FileStatusIconItem = memo(function ({changeType, isSymlink}: {changeType: string; isSymlink?: boolean}) {
  return <FileStatusIcon status={changeType} isSymlink={isSymlink} />
})

FileStatusIconItem.displayName = 'FileStatusIconItem'

export const File = memo(function File({file, depth, onSelect}: FileProps) {
  const rowRef = useRef<HTMLElement>(null)
  const anchorRef = useRef<HTMLAnchorElement>(null)
  const anchorHref = useMemo(() => `#diff-${file.diff.pathDigest}`, [file.diff.pathDigest])
  const showTooltip = useFileTreeTooltip({focusRowRef: rowRef, mouseRowRef: rowRef})
  const totalCommentsCount = file.diff.totalCommentsCount ?? 0
  const maxAnnotation = file.diff.highestAnnotationLevel
  const updateSelectedPathDigestAndFetch = useUpdateSelectedPathDigestAndFetch()
  const isSelectedFile = useIsSelectedFile(file.diff.pathDigest)
  const {sendAnalyticsEvent} = useAnalytics()
  const getINPDiffTags = useGetINPDiffTags()

  const {screenReaderText, annotationElement} = useMemo(() => {
    let text = file.fileName ?? 'This file'
    let element = null

    if (totalCommentsCount > 0) {
      text += ` has ${totalCommentsCount < 10 ? totalCommentsCount : '9+'} ${
        totalCommentsCount > 1 ? 'comments' : 'comment'
      }`
    }

    switch (maxAnnotation) {
      case 'WARNING':
        text += `${totalCommentsCount > 0 ? ' and' : ''} has warning annotations`
        element = (
          <div className="pl-1 fgColor-attention">
            <AlertIcon />
          </div>
        )
        break
      case 'NOTICE':
        text += `${totalCommentsCount > 0 ? ' and' : ''} has notice annotations`
        element = (
          <div className="pl-1 fgColor-default">
            <InfoIcon />
          </div>
        )
        break
      case 'FAILURE':
        text += `${totalCommentsCount > 0 ? ' and' : ''} has failure annotations`
        element = (
          <div className="pl-1 fgColor-danger">
            <XCircleFillIcon />
          </div>
        )
        break
    }

    return {screenReaderText: text, annotationElement: element}
  }, [file.fileName, totalCommentsCount, maxAnnotation])

  useEffect(() => {
    if (rowRef.current && isSelectedFile) {
      const timeout = window.setTimeout(() => {
        //this is obviously brittle and not testable, but is the cleanest solution to avoid jumpiness
        const fileTreeParent = ssrSafeDocument?.getElementById(DIFF_FILE_TREE_ID)?.parentElement
        const rowHeight = rowRef.current!.offsetTop
        const innerWindowHeight = ssrSafeWindow?.innerHeight ?? 0
        const usableHeight = innerWindowHeight / 2
        if (fileTreeParent) fileTreeParent.scrollTop = rowHeight - usableHeight
      }, 0)

      return () => {
        window.clearTimeout(timeout)
      }
    }
  }, [file.diff.pathDigest, isSelectedFile])

  const onSelectItem = useCallback(
    (e: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>) => {
      const tags = getINPDiffTags()
      // Keyboard events: trigger selection only
      if (e.nativeEvent instanceof KeyboardEvent) {
        const keyboardEvent = e as KeyboardEvent<HTMLElement>
        // Do nothing if item isn't currently selected
        if (rowRef?.current !== document.activeElement) {
          keyboardEvent.preventDefault()
          return
        }
        // Trigger primary action when Enter or Space are pressed
        if (keyboardEvent.key === 'Enter' || keyboardEvent.key === ' ') {
          keyboardEvent.preventDefault()

          // Send DD metric when user selects diff file with a keyboard, to scroll to diff in the same window.
          getGlobalINPObserver()?.registerCallback({
            event: e.nativeEvent,
            cb: interaction => {
              sendAnalyticsEvent('diff-lines.inp_interaction', 'PULL_REQUESTS_FILE_TREE_DIFF_FILE_KEYBOARD_SELECTION', {
                latency: interaction.latency,
              })
              sendCustomMetric({
                name: 'PULL_REQUESTS_FILE_TREE_DIFF_FILE_KEYBOARD_SELECTION',
                tags,
                value: interaction.latency,
              })
            },
          })

          updateSelectedPathDigestAndFetch(file.diff.pathDigest)
          updateUrlHash(`diff-${file.diff.pathDigest}`)
          addUrlToHistoryStack(window.location.href)
          onSelect?.()
          // Scroll to selected diff entry
          ssrSafeWindow?.requestAnimationFrame(() => {
            scrollToElementWithOffset(document.getElementById(`diff-${file.diff.pathDigest}`))
          })

          return
        }
      }
      if (e.nativeEvent instanceof MouseEvent) {
        const mouseEvent = e as MouseEvent<HTMLElement>
        // Open link in a new tab on click with command or control pressed or middle click
        if (mouseEvent.metaKey || mouseEvent.ctrlKey || mouseEvent.button === 1) {
          mouseEvent.preventDefault()
          window.open(anchorHref, '_blank')
          return
        }

        // Send DD metric when user selects diff file with a mouse click, to scroll to diff in the same window.
        getGlobalINPObserver()?.registerCallback({
          event: e.nativeEvent,
          cb: interaction => {
            sendAnalyticsEvent('diff-lines.inp_interaction', 'PULL_REQUESTS_FILE_TREE_DIFF_FILE_CLICK', {
              latency: interaction.latency,
            })
            sendCustomMetric({
              name: 'PULL_REQUESTS_FILE_TREE_DIFF_FILE_CLICK',
              tags,
              value: interaction.latency,
            })
          },
        })

        updateSelectedPathDigestAndFetch(file.diff.pathDigest)
        updateUrlHash(`diff-${file.diff.pathDigest}`)
        addUrlToHistoryStack(window.location.href)
        onSelect?.()
        // Scroll to selected diff entry
        ssrSafeWindow?.requestAnimationFrame(() => {
          scrollToElementWithOffset(document.getElementById(`diff-${file.diff.pathDigest}`))
        })
      }
    },
    [updateSelectedPathDigestAndFetch, file.diff.pathDigest, onSelect, sendAnalyticsEvent, getINPDiffTags, anchorHref],
  )

  const fileName = file.fileName ?? ''

  const fileLinkElement = (
    <Link
      href={anchorHref}
      muted
      ref={anchorRef}
      // Intentionally setting the role to presentation to prevent link from being announced by assistive tech
      role="presentation"
      className="fgColor-default"
      // Intentionally setting tabIndex to -1 to prevent link from being focusable by assistive tech
      tabIndex={-1}
      // Prevent the link from being focused or clicked separately from the TreeView.Item
      onClick={(e: MouseEvent) => e.preventDefault()}
    >
      {fileName}
    </Link>
  )

  return (
    <>
      <TreeView.Item
        defaultExpanded
        aria-label={screenReaderText}
        aria-level={depth}
        current={isSelectedFile}
        id={file.diff.path}
        key={file.diff.pathDigest}
        onSelect={onSelectItem}
        ref={rowRef}
        className={styles['file-tree-row']}
      >
        <TreeView.LeadingVisual>
          <FileStatusIconItem changeType={file.diff.changeType} isSymlink={file.diff.isSymlink} />
        </TreeView.LeadingVisual>
        {showTooltip ? (
          <Tooltip text={fileName} direction="ne">
            {fileLinkElement}
          </Tooltip>
        ) : (
          fileLinkElement
        )}
        {/* Don't show the unresolved comment and annotation count if it's 0 */}
        {(!!totalCommentsCount || maxAnnotation) && (
          <TreeView.TrailingVisual>
            <div className="d-flex flex-items-center flex-row">
              {!!totalCommentsCount && (
                <>
                  <CommentIcon />
                  <div className="ml-1 text-bold fgColor-default f6">
                    {totalCommentsCount < 10 ? totalCommentsCount : '9+'}
                  </div>
                </>
              )}
              {annotationElement}
            </div>
          </TreeView.TrailingVisual>
        )}
      </TreeView.Item>
    </>
  )
})

try{ File.displayName ||= 'File' } catch {}